import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('theme') === 'dark' ? 'dark' : 'light';
    } catch (error) {
      console.warn('Theme preference could not be read:', error);
      return 'light';
    }
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('theme', theme);
    } catch (error) {
      console.warn('Theme preference could not be saved:', error);
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(previous => previous === 'light' ? 'dark' : 'light');
  };

  const muiTheme = useMemo(() => createTheme({
    palette: {
      mode: theme,
      ...(theme === 'dark' ? {
        primary: { main: '#ef5350' }, // madder (light red)
        secondary: { main: '#4dd0e1' }, // pacific-cyan (light cyan)
        background: {
          default: '#121212',
          paper: '#1e1e1e',
        },
        text: {
          primary: '#e0e0e0',
          secondary: '#b39ddb',
        },
      } : {
        primary: { main: '#D62839' }, // madder
        secondary: { main: '#4ba3c3' }, // pacific-cyan
        background: {
          default: '#ffffff',
          paper: '#ffffff',
        },
        text: {
          primary: '#000000',
          secondary: '#836b89',
        },
      }),
    },
    typography: {
      fontFamily: '"Segoe UI", "Roboto", "Arial", sans-serif',
    },
  }), [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
